import React, { createContext, useState, useEffect, useContext } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { StyledEngineProvider } from "@mui/material/styles";

import CssBaseline from "./CssBaseline";
import { buildMuiTheme } from "./theme/muiTheme";
import { lightPalette, darkPalette } from "./themes/muiPalette";

/** Retrieve the persisted theme mode ("light" | "dark"), or null if unset. */
const getStoredThemeMode = (): string | null => localStorage.getItem("appTheme");
/** Persist the theme mode so it survives page refreshes. */
const setStoredThemeMode = (mode: string): void => { localStorage.setItem("appTheme", mode); };

export interface ThemeContextValue {
  themeMode: "light" | "dark";
  toggleTheme: (mode: "light" | "dark") => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  themeMode: "light",
  toggleTheme: () => undefined,
});

export function useThemeMode(): ThemeContextValue {
  return useContext(ThemeContext);
}

export const ThemeProviderWrapper = ({ children }: { children?: React.ReactNode }) => {
  const [themeMode, setThemeMode] = useState<"light" | "dark">("light");

  // ✅ Optional: remember theme via layout domain helper
  useEffect(() => {
    const savedTheme = getStoredThemeMode();
    if (savedTheme === "light" || savedTheme === "dark") {
      setThemeMode(savedTheme);
    }
  }, []);

  const toggleTheme = (mode: "light" | "dark") => {
    setThemeMode(mode);
    setStoredThemeMode(mode);
  };

  // Sync the data-theme attribute on <html> so CSS variable overrides
  // in index.css ([data-theme="dark"]) activate automatically.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeMode);
  }, [themeMode]);

  const activePalette = themeMode === "light" ? lightPalette : darkPalette;

  // Start from the full token-driven theme (typography, spacing, component
  // overrides, etc.) and layer the light/dark palette + CssBaseline body
  // styles on top so the entire app gets a consistent visual baseline.
  const baseTheme = buildMuiTheme();
  const theme = createTheme(baseTheme, {
    palette: {
      mode: themeMode,
      background: activePalette.background,
      text: { primary: activePalette.text.primary, secondary: activePalette.text.secondary },
      divider: activePalette.divider,
      action: {
        active: activePalette.action.active,
        hover: activePalette.action.hover,
        selected: activePalette.action.selected,
        disabled: activePalette.action.disabled,
        disabledBackground: activePalette.action.disabledBackground,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor:
              themeMode === "light"
                ? "var(--color-bg-default-hex, #f5f5f5)"
                : "var(--color-bg-default-dark-hex, #121212)",
            color:
              themeMode === "light"
                ? "var(--color-text-primary-hex, #000)"
                : "var(--color-text-primary-dark-hex, #fff)",
          },
        },
      },
      // Chip: ensure label & icon text adapt for dark backgrounds
      MuiChip: {
        styleOverrides: {
          root: {
            color: activePalette.text.primary,
          },
          outlined: {
            borderColor: activePalette.divider,
          },
        },
      },
      // Paper: all popup surfaces (Select dropdowns, Autocomplete, Popovers)
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: activePalette.background.paper,
            color: activePalette.text.primary,
          },
        },
      },
      // InputBase: readable text inside all inputs (Select, TextField, Autocomplete)
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: activePalette.text.primary,
          },
          input: {
            color: activePalette.text.primary,
            '&::placeholder': {
              color: activePalette.text.secondary,
              opacity: 1,
            },
          },
        },
      },
      // OutlinedInput: border color adapts for dark mode
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: activePalette.text.primary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: activePalette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: activePalette.text.secondary,
            },
          },
        },
      },
      // InputLabel: readable labels above form fields
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: activePalette.text.secondary,
            '&.Mui-focused': {
              color: themeMode === "dark" ? "#60A5FA" : undefined,
            },
          },
        },
      },
      // Checkbox: visible in both themes
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: activePalette.text.secondary,
          },
        },
      },
      // Autocomplete: popup dropdown adapts for dark mode
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: activePalette.background.paper,
            color: activePalette.text.primary,
          },
          listbox: {
            color: activePalette.text.primary,
          },
          option: {
            '&[aria-selected="true"]': {
              backgroundColor: activePalette.action.selected,
            },
            '&:hover': {
              backgroundColor: activePalette.action.hover,
            },
          },
          noOptions: {
            color: activePalette.text.secondary,
          },
          tag: {
            color: activePalette.text.primary,
          },
        },
      },
      // FormLabel: readable form labels
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: activePalette.text.secondary,
          },
        },
      },
      // ListItemText: readable text in dropdown list items
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: activePalette.text.primary,
          },
          secondary: {
            color: activePalette.text.secondary,
          },
        },
      },
      // Link: preserve blue for links in dark mode
      MuiLink: {
        styleOverrides: {
          root: {
            color: themeMode === "dark" ? "#60A5FA" : undefined,
          },
        },
      },
      // Menu paper: dark background for dropdowns
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: activePalette.background.paper,
            color: activePalette.text.primary,
          },
        },
      },
      // MenuItem: readable text in menus
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: activePalette.text.primary,
          },
        },
      },
      // Select: readable text in select elements
      MuiSelect: {
        styleOverrides: {
          select: {
            color: activePalette.text.primary,
          },
          icon: {
            color: activePalette.text.secondary,
          },
        },
      },
      // Drawer: dark sidebar background
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: activePalette.background.default,
          },
        },
      },
      // Tab: blue selected text, readable inactive text
      MuiTab: {
        styleOverrides: {
          root: {
            color: activePalette.text.secondary,
            "&.Mui-selected": {
              color: "#2563EB",
            },
          },
        },
      },
      // Tab indicator: blue underline
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: "#2563EB",
          },
        },
      },
      // ListItemButton: blue left border + blue text when selected
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderLeft: "3px solid transparent",
            "&.Mui-selected": {
              borderLeftColor: "#2563EB",
              color: "#2563EB",
              backgroundColor: themeMode === "dark"
                ? "rgba(37, 99, 235, 0.12)"
                : "rgba(37, 99, 235, 0.08)",
              "&:hover": {
                backgroundColor: themeMode === "dark"
                  ? "rgba(37, 99, 235, 0.18)"
                  : "rgba(37, 99, 235, 0.12)",
              },
            },
          },
        },
      },
      // Tooltip: proper contrast
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor:
              themeMode === "dark" ? "#374151" : undefined,
            color: themeMode === "dark" ? "#F1F5F9" : undefined,
          },
        },
      },
      // Dialog: dark background
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: activePalette.background.paper,
            color: activePalette.text.primary,
          },
        },
      },
    },
  });

  return (
    <StyledEngineProvider injectFirst>
      <ThemeContext.Provider value={{ themeMode, toggleTheme }}>
        <ThemeProvider theme={theme}>
          {/* 🔑 Applies global background/text styles */}
          <CssBaseline />
          {children}
        </ThemeProvider>
      </ThemeContext.Provider>
    </StyledEngineProvider>
  );
};

